import React from "react";
import { Flex, Tag, Tooltip } from "@chakra-ui/react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { qoriaColours } from "@/theme/colours";
import { Rule } from "./types";

/** Where the rule on the other side of a conflict or override lives. */
export type RuleSource = { kind: "inClass" } | { kind: "list"; name: string };

export type RuleSourceById = Record<string, RuleSource>;

interface Props {
	rules: Pick<Rule, "id" | "conflictedBy" | "overriddenBy">[];
	sourceByRuleId: RuleSourceById;
}

type Relationship = {
	key: string;
	/** Short badge text, e.g. "Overridden". Kept source-free so the badge is always one predictable size. */
	label: string;
	/** Full explanation for the tooltip. Undefined when the related rule is not shown in the drawer. */
	reason?: string;
};

export function RuleRelationshipTags({ rules, sourceByRuleId }: Props): JSX.Element | null {
	const { t } = useTranslation();
	const relationships = new Map<string, Relationship>();

	function sourceKey(source: RuleSource | undefined): string {
		if (source === undefined) {
			return "";
		}
		return source.kind === "inClass" ? "inClass" : `list:${source.name}`;
	}

	for (const rule of rules) {
		if (rule.conflictedBy) {
			const source = sourceByRuleId[rule.conflictedBy];
			const key = `conflict:${sourceKey(source)}`;
			relationships.set(key, {
				key,
				label: t("rulesLibrary.activeRules.conflictWithoutSource"),
				reason: explain(t, "conflict", source),
			});
		}
		if (rule.overriddenBy) {
			const source = sourceByRuleId[rule.overriddenBy];
			const key = `overridden:${sourceKey(source)}`;
			relationships.set(key, {
				key,
				label: t("rulesLibrary.activeRules.overriddenWithoutSource"),
				reason: explain(t, "overridden", source),
			});
		}
	}

	if (relationships.size === 0) {
		return null;
	}

	return (
		<Flex align="center" gap={1} flexShrink={0}>
			{[...relationships.values()].map((relationship) => (
				<Tooltip key={relationship.key} label={relationship.reason} isDisabled={relationship.reason === undefined} shouldWrapChildren>
					<Tag
						bg={qoriaColours.brand.warning[50]}
						color={qoriaColours.brand.warning[700]}
						fontWeight={500}
						gap={1}
						flexShrink={0}
						whiteSpace="nowrap"
						aria-label={relationship.reason}
					>
						<ChakraFAIcon
							icon="fa-triangle-exclamation"
							variant="solid"
							boxSize={3.5}
							color={qoriaColours.brand.warning[400]}
							flexShrink={0}
						/>
						{relationship.label}
					</Tag>
				</Tooltip>
			))}
		</Flex>
	);
}

/** Builds the tooltip sentence, which names the kind of source rather than just its name. */
function explain(t: TFunction, relationship: "conflict" | "overridden", source: RuleSource | undefined): string | undefined {
	if (source === undefined) {
		return undefined;
	}
	if (source.kind === "inClass") {
		return t(`rulesLibrary.activeRules.${relationship}TooltipInClass`);
	}
	return t(`rulesLibrary.activeRules.${relationship}TooltipList`, { name: source.name });
}
